import { useCallback, useEffect, useMemo } from 'react'
import { QueryClient, useCoreConfig } from '@gaddario98/react-core'
import { useAuthState } from '@gaddario98/react-core/auth'
import { useNotification } from '@gaddario98/react-core/notifications'
import { useTranslation } from '@gaddario98/react-core/localization'
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { storage } from '@gaddario98/react-core/state'
import { useFirebaseAuthProvider, useFirebaseConfig } from '../../firebase'
import itTranslations from '../../locales/it.json'
import { useAuthenticatedUser } from './useAuthenticatedUser'
import type { AuthState } from '@gaddario98/react-core/auth'
import { FeaturesSection, FooterSection, HeroSection } from '@/features/home'
import { useUserDataState } from '@/features/users'
import { PageContainer } from '@/ui'

const API = import.meta.env.VITE_API_URL || 'http://localhost:3000'
export const asyncStoragePersister = createAsyncStoragePersister({
  storage: storage,
})

export const appQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      staleTime: 2 * 60 * 1000, // 2 minutes
      refetchOnMount: true,
      refetchInterval: 2 * 60 * 1000, // 2 minutes
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: true,
    },
  },
});

// Helper to create a storage persister if needed outside of the provider
export const createStoragePersister = (st: Storage) => {
  return createAsyncStoragePersister({ storage: st })
}
export const useAppConfig = () => {
  const [auth, setAuth] = useAuthState()
  const userId = auth?.id ?? ''
  const isLogged = Boolean(auth?.isLogged)
  const { data: user } = useAuthenticatedUser(userId, isLogged)
  const [, setUserData] = useUserDataState()
  const { t: translateText } = useTranslation()
  const { t: translateTextAuth } = useTranslation('auth')
  const { showNotification } = useNotification()

  useFirebaseAuthProvider({
    setFirebaseAuth: useCallback(
      (firebaseAuth) => {
        setAuth(firebaseAuth ? firebaseAuth : null)
      },
      [setAuth],
    ),
    initializeNotifications: useCallback(() => {
      return Promise.resolve()
    }, []),
  })

  useFirebaseConfig({
    showNotification,
    translateText: translateTextAuth,
  })

  useCoreConfig({
    apiConfig: useMemo(
      () => ({
        endpoints: { default: API, users: API + '/users', api: API },
        defaultHeaders: {
          Authorization: auth?.token ? `Bearer ${auth.token}` : '',
        },
        validateAuthFn: () => !!auth?.isLogged,
        showNotification,
        persistOptions: {
          persister: asyncStoragePersister,
          maxAge: Infinity,
          buster: 'persister-v1',
        }, queryClient: appQueryClient
      }),
      [auth, showNotification],
    ),
    localization: useMemo(
      () => ({ currentLocale: 'it', locales: { it: itTranslations } }),
      [],
    ),
    pages: useMemo(
      () => ({
        PageContainer: PageContainer,
        isLogged: (authState: AuthState | null) =>
          !!authState?.id && !!authState.isLogged,
        authValues: auth,
        authPageProps: {
          id: 'auth-redirect',
          viewSettings: {
            pageContainerComponent: ({ children }) => <>{children}</>,
          },
          contents: [
            {
              type: 'custom',
              component: (
                <div className="flex flex-col min-h-screen">
                  <HeroSection ctaLinkMode="anchor" />
                  <FeaturesSection />
                  <FooterSection />
                </div>
              ),
            },
          ],
        },
      }),
      [auth],
    ),
    form: useMemo(
      () => ({
        translateText,
        showNotification,
      }),
      [showNotification, translateText],
    ),
  })

  useEffect(() => {
    if (!isLogged || user?.uid !== userId) {
      setUserData(undefined)
      return
    }

    setUserData(user)
  }, [isLogged, setUserData, user, userId])

  return auth
}
